<!-- src/components/ListingCreator.vue -->
<template>
  <div class="listing-creator">
    <!-- Spinner Modal -->
    <!-- <div v-if="isProcessing" class="spinner-modal">
      <div class="spinner-modal-content">
        <span class="spinner spinner-lg"></span>
        <div class="spinner-text">Creating Listings...</div>
      </div>
    </div> -->
    <div class="form-section" v-if="!isProcessing && !hasErrors && !isCompleted">
      <h2>Create Listings</h2>
      
      <!-- Input form -->
      <form @submit.prevent="createListing" class="listing-form">
        <div class="form-group">
          <label>Event IDs (comma separated)</label>
          <input 
            v-model="formData.eventIds" 
            type="text" 
            placeholder="123,456,789"
            required
            :disabled="isSubmitting"
            @input="validateInput"
          >
          <span class="help-text">Enter event IDs separated by commas or spaces</span>
          <span v-if="inputError" class="error-text">{{ inputError }}</span>
        </div>
        
        <div class="form-group">
          <label>Number of Tickets</label>
          <input 
            v-model="formData.numberOfTickets" 
            type="text" 
            :placeholder="defaultValues.number_of_tickets"
            :disabled="isSubmitting"
          >
        </div>
        
        <div class="form-group">
          <label>Shown Quantity</label>
          <input 
            v-model="formData.shownQty" 
            type="text" 
            :placeholder="defaultValues.shown_qty"
            :disabled="isSubmitting"
          >
        </div>
        
        <div class="form-group">
          <label>Exchanges</label>
          <div class="checkbox-group">
            <label>
              <input 
                type="checkbox" 
                value="stubhub" 
                v-model="formData.exchanges"
                :disabled="isSubmitting"
              > StubHub
            </label>
            <label>
              <input 
                type="checkbox" 
                value="vividseats" 
                v-model="formData.exchanges"
                :disabled="isSubmitting"
              > VividSeats
            </label>
            <label>
              <input 
                type="checkbox" 
                value="seatgeek" 
                v-model="formData.exchanges"
                :disabled="isSubmitting"
              > Seatgeek
            </label>
          </div>
        </div>
        
        <button 
          type="submit" 
          :disabled="isSubmitting || !isFormValid || inputError"
          class="submit-button"
        >
          <span v-if="isSubmitting" class="spinner"></span>
          {{ isSubmitting ? 'Creating...' : 'Create Listings' }}
        </button>
      </form>
    </div>

    <!-- Progress section -->
    <div v-if="isProcessing || hasErrors || isCompleted" class="progress-section">
      <div class="progress-header">
        <h3>{{ getStatusTitle }}</h3>
        <button 
          v-if="!isProcessing && hasErrors" 
          @click="returnToForm" 
          class="retry-button"
        >
          Return to Events
        </button>
        <button 
          v-if="!isProcessing && isCompleted && !hasErrors" 
          @click="returnToForm" 
          class="return-button"
        >
          Return to Events
        </button>
      </div>
      
      <!-- Error Summary -->
      <div v-if="errorSummary.length > 0" class="error-summary">
        <h4>
          <i class="fas fa-exclamation-triangle"></i>
          Errors Encountered:
        </h4>
        <ul>
          <li v-for="(error, index) in errorSummary" 
              :key="index" 
              class="error-item"
          >
            {{ error }}
          </li>
        </ul>
      </div>
      
      <!-- Progress Stats -->
      <div class="progress-stats">
        <div class="stat-item">
          <span class="stat-label">Total Events:</span>
          <span class="stat-value">{{ totalEvents }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Processed:</span>
          <span class="stat-value">{{ processedEvents }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Success:</span>
          <span class="stat-value success">{{ successfulEvents }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Failed:</span>
          <span class="stat-value error">{{ failedEvents }}</span>
        </div>
      </div>
            <!-- Progress Bar -->
            <div class="progress-container">
        <div class="progress-bar">
          <div 
            :style="{ width: `${currentProgress}%` }" 
            :class="['progress-fill', { 'error': hasErrors }]"
          ></div>
        </div>
        <div class="progress-percentage">{{ currentProgress }}%</div>
      </div>
      
      <div :class="['status-message', { 'error-text': hasErrors }]">
        {{ currentMessage }}
      </div>

      <!-- Progress log -->
      <div class="progress-log">
        <div class="log-header">
          <h4>Processing Log</h4>
          <button @click="clearLog" class="clear-log-button">
            Clear Log
          </button>
        </div>
        
        <div class="log-entries">
          <div 
            v-for="(update, index) in progressLog" 
            :key="index"
            :class="['log-entry', update.status]"
          >
            <div class="log-entry-header">
              <span class="timestamp">{{ formatTime(update.timestamp) }}</span>
              <span :class="['status-badge', update.status]">
                {{ update.status.toUpperCase() }}
              </span>
            </div>
            
            <div class="message">{{ update.message }}</div>
            
            <div v-if="update.details" class="details">
              <div v-if="update.details.error" class="error-details">
                <strong>Error Details:</strong>
                <pre>{{ update.details.error }}</pre>
              </div>
              <pre>{{ JSON.stringify(omitErrorFromDetails(update.details), null, 2) }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <div 
        v-for="(toast, index) in toasts" 
        :key="index"
        :class="['toast', toast.type]"
        @click="removeToast(index)"
      >
        {{ toast.message }}
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'

export default {
  name: 'ListingCreator',
  props: {
    selectedEvents: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      API_BASE_URL: 'https://api.spotjockeys.com', // Update with your API base URL
      defaultValues: {
        number_of_tickets: "50",
        shown_qty: "2",
        exchanges: ["stubhub", "vividseats"]
      },
      formData: {
        eventIds: this.selectedEvents,
        numberOfTickets: "50",
        shownQty: "2",
        exchanges: ["stubhub", "vividseats"]
      },
      isSubmitting: false,
      isProcessing: false,
      hasErrors: false,
      currentProgress: 0,
      currentMessage: '',
      progressLog: [],
      errorSummary: [],
      eventSource: null,
      toasts: [],
      totalEvents: 0,
      processedEvents: 0,
      successfulEvents: 0,
      failedEvents: 0,
      inputError: null,
      isCompleted: false,
      pollInterval: null
    }
  },

  computed: {
    isFormValid() {
      return this.formData.exchanges.length > 0
    },

    getStatusTitle() {
      if (this.hasErrors && !this.isProcessing) {
        return 'Process Failed'
      }
      if (this.hasErrors && this.isProcessing) {
        return 'Processing (With Errors)'
      }
      return 'Creating Listings...'
    }
  },
  methods: {
    formatTime(timestamp) {
      return format(new Date(timestamp), 'HH:mm:ss')
    },

    validateEventIds(input) {
      if (!input) {
        throw new Error('Event IDs are required')
      }

      let eventIds = []

      // If input is already an array
      if (Array.isArray(input)) {
        eventIds = input
      } 
      // If input is a string
      else if (typeof input === 'string') {
        // Handle different separators (, or space or newline)
        eventIds = input
          .split(/[,\s]+/)
          .map(id => id.trim())
          .filter(id => id)
      } 
      else {
        throw new Error('Invalid event IDs format')
      }

      if (eventIds.length === 0) {
        throw new Error('At least one event ID is required')
      }

      // Validate each event ID
      eventIds.forEach(id => {
        if (!/^\d+$/.test(id)) {
          throw new Error(`Invalid event ID format: ${id}`)
        }
      })

      return eventIds
    },

    validateInput() {
      try {
        this.validateEventIds(this.formData.eventIds)
        this.inputError = null
      } catch (error) {
        this.inputError = error.message
      }
    },

    showToast(message, type = 'info') {
      const toast = { message, type }
      this.toasts.push(toast)
      setTimeout(() => this.removeToast(this.toasts.indexOf(toast)), 5000)
    },

    removeToast(index) {
      this.toasts.splice(index, 1)
    },

    omitErrorFromDetails(details) {
      const { error, ...rest } = details
      console.log(error)
      return rest
    },

    clearLog() {
      this.progressLog = []
    },

    resetAndRetry() {
      this.hasErrors = false
      this.errorSummary = []
      this.isProcessing = false
      this.currentProgress = 0
      this.currentMessage = ''
      this.progressLog = []
      this.totalEvents = 0
      this.processedEvents = 0
      this.successfulEvents = 0
      this.failedEvents = 0
      this.isCompleted = false
      this.createListing()
    },

    returnToForm() {
      // Reset all relevant state
      this.isCompleted = false
      this.progressLog = []
      this.currentProgress = 0
      this.currentMessage = ''
      this.totalEvents = 0
      this.processedEvents = 0
      this.successfulEvents = 0
      this.failedEvents = 0
      this.hasErrors = false
      this.isProcessing = false
      this.resetForm()
      this.$emit('hide-listing-creator')
      // Refresh the page
      window.location.reload()
    },

    resetForm() {
      this.formData = {
        eventIds: this.selectedEvents,
        numberOfTickets: "50",
        shownQty: "2",
        exchanges: ["stubhub",  "vividseats"]
      }
      this.inputError = null
    },

    async createListing() {
      try {
        this.isSubmitting = true
        
        let eventIds
        try {
          eventIds = this.validateEventIds(this.formData.eventIds)
        } catch (error) {
          this.showToast(error.message, 'error')
          return
        }
        
        this.totalEvents = eventIds.length
        
        const requestData = {
          event_ids: eventIds,
          number_of_tickets: this.formData.numberOfTickets || this.defaultValues.number_of_tickets,
          shown_qty: this.formData.shownQty || this.defaultValues.shown_qty,
          exchanges: this.formData.exchanges
        }

        console.log('Sending request with data:', requestData)

        const response = await fetch(`${this.API_BASE_URL}/api/create-listing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.isProcessing = true
        this.listenToProgress(data.job_id)
        
      } catch (error) {
        this.showToast(`Failed to start process: ${error.message}`, 'error')
        this.hasErrors = true
        this.errorSummary.push(error.message)
      } finally {
        this.isSubmitting = false
      }
    },

    handleUpdateStatus(update) {
      this.currentProgress = update.progress
      this.currentMessage = update.message
      this.progressLog.unshift(update)

      // Update statistics
      if (update.details?.event_id) {
        this.processedEvents = new Set(
          this.progressLog
            .filter(log => log.details?.event_id)
            .map(log => log.details.event_id)
        ).size
      }

      // Handle errors
      if (update.status === 'error') {
        this.hasErrors = true
        this.failedEvents++
        const errorMessage = update.details?.error || update.message
        if (!this.errorSummary.includes(errorMessage)) {
          this.errorSummary.push(errorMessage)
          this.showToast(errorMessage, 'error')
        }
      }

      // Handle success
      if (update.status === 'success') {
        this.successfulEvents++
        this.showToast(update.message, 'success')
      }

      // Handle completion
      if (update.status === 'completed' || 
          (update.status === 'error' && update.progress === 100)) {
        this.eventSource?.close()
        clearInterval(this.pollInterval)
        this.isProcessing = false
        this.isCompleted = true
        this.$emit('complete', update)
        
        if (!this.hasErrors) {
          this.showToast('All listings created successfully!', 'success')
          this.$emit('reset-events')
        }
      }
    },

    listenToProgress(jobId) {
      if (this.eventSource) {
        this.eventSource.close()
      }

      this.eventSource = new EventSource(`${this.API_BASE_URL}/api/listing-progress/${jobId}`)

      this.eventSource.onmessage = (event) => {
        const update = JSON.parse(event.data)
        this.handleUpdateStatus(update)
      }

      this.eventSource.onerror = (error) => {
        console.error('SSE Error:', error)
        this.eventSource.close()
        this.isProcessing = false
        this.hasErrors = true
        const errorMessage = 'Lost connection to server'
        this.errorSummary.push(errorMessage)
        this.showToast(errorMessage, 'error')
        this.$emit('error', error)
      }

      this.pollInterval = setInterval(() => {
        fetch(`${this.API_BASE_URL}/api/listing-progress/${jobId}`)
          .then(response => response.json())
          .then(data => {
            data.forEach(update => this.handleUpdateStatus(update))
          })
          .catch(error => {
            console.error('Polling Error:', error)
          })
      }, 3000) // Poll every 5 seconds
    }
  },

  beforeUnmount() {
    if (this.eventSource) {
      this.eventSource.close()
    }
    clearInterval(this.pollInterval)
  }
}
</script>
<style scoped>
.listing-creator {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

/* Form Styles */
.listing-form {
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2d3748;
}

.form-group input[type="text"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input[type="text"]:focus {
  border-color: #4299e1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #718096;
}

.error-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #e53e3e;
}

.checkbox-group {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

/* Progress Section Styles */
.progress-section {
  margin-top: 30px;
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.progress-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
  background: #f7fafc;
  padding: 15px;
  border-radius: 6px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #718096;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.stat-value.success {
  color: #48bb78;
}

.stat-value.error {
  color: #e53e3e;
}

/* Progress Bar Styles */
.progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.progress-bar {
  flex-grow: 1;
  height: 8px;
  background-color: #edf2f7;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4299e1;
  transition: width 0.3s ease;
}

.progress-fill.error {
  background-color: #e53e3e;
}

.progress-percentage {
  min-width: 45px;
  font-size: 14px;
  font-weight: 500;
  color: #718096;
}

/* Error Summary Styles */
.error-summary {
  margin: 15px 0;
  padding: 15px;
  background-color: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
}

.error-summary h4 {
  color: #c53030;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-item {
  color: #c53030;
  margin-bottom: 8px;
  font-size: 14px;
}

/* Log Styles */
.progress-log {
  margin-top: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.log-header h4 {
  margin: 0;
  color: #2d3748;
}

.log-entries {
  max-height: 400px;
  overflow-y: auto;
  padding: 15px;
}

.log-entry {
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.log-entry:last-child {
  margin-bottom: 0;
}

.log-entry.error {
  border-color: #feb2b2;
  background-color: #fff5f5;
}

.log-entry.warning {
  border-color: #fbd38d;
  background-color: #fffaf0;
}

.log-entry.success {
  border-color: #9ae6b4;
  background-color: #f0fff4;
}

.log-entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.timestamp {
  font-size: 12px;
  color: #718096;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.error {
  background-color: #fff5f5;
  color: #c53030;
}

.status-badge.warning {
  background-color: #fffaf0;
  color: #c05621;
}

.status-badge.success {
  background-color: #f0fff4;
  color: #2f855a;
}

.status-badge.in_progress {
  background-color: #ebf8ff;
  color: #2b6cb0;
}

.message {
  margin: 8px 0;
  color: #2d3748;
}

.details {
  margin-top: 8px;
  padding: 8px;
  background-color: #f7fafc;
  border-radius: 4px;
  font-size: 13px;
}

.error-details {
  margin-bottom: 8px;
  padding: 8px;
  background-color: #fff5f5;
  border-left: 3px solid #e53e3e;
}

.error-details strong {
  color: #c53030;
}

/* Button Styles */
.submit-button, .retry-button, .clear-log-button, .return-button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-button {
  background-color: #4299e1;
  color: white;
}

.submit-button:hover {
  background-color: #3182ce;
}

.submit-button:disabled {
  background-color: #cbd5e0;
  cursor: not-allowed;
}

.retry-button {
  background-color: #48bb78;
  color: white;
}

.retry-button:hover {
  background-color: #38a169;
}

.clear-log-button {
  background-color: #edf2f7;
  color: #4a5568;
  font-size: 12px;
  padding: 6px 12px;
}

.clear-log-button:hover {
  background-color: #e2e8f0;
}

.return-button {
  background-color: #4299e1;
  color: white;
}

.return-button:hover {
  background-color: #3182ce;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
}

.toast {
  padding: 12px 20px;
  border-radius: 6px;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  animation: slideIn 0.3s ease;
  max-width: 300px;
}

.toast.error {
  background-color: #fff5f5;
  border-left: 4px solid #e53e3e;
  color: #c53030;
}

.toast.success {
  background-color: #f0fff4;
  border-left: 4px solid #48bb78;
  color: #2f855a;
}

.toast.info {
  background-color: #ebf8ff;
  border-left: 4px solid #4299e1;
  color: #2b6cb0;
}

/* Loading Spinner */

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .listing-creator {
    padding: 10px;
  }

  .progress-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .checkbox-group {
    flex-direction: column;
    gap: 10px;
  }
}

.spinner-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(30, 41, 59, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.spinner-modal-content {
  background: #fff;
  padding: 40px 32px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.spinner-lg {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #4299e1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 18px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.spinner-text {
  font-size: 1.1em;
  color: #2d3748;
  margin-top: 8px;
}
.spinner {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #4299e1;
  border-radius: 50%;
  border-style: solid;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>